'use client';

import React, { useEffect, useMemo, useState } from 'react';
import { faMoneyCheckDollarPen } from '@fortawesome/pro-solid-svg-icons';
import {
  Card,
  Typography,
  CircularProgress,
  ProfileCardHeader,
} from 'src/components';
import {
  ReimbursementCard,
  AllSitesSelect,
  AllPayersSelect,
  NoReimbursementData,
} from './components';
import useGetPayerMixReimbursement from 'src/hooks/payerMix/useGetPayerMixReimbursement';
import useProfileSelections from 'src/hooks/profiles/useProfileSelections';
import useUserSettings from 'src/hooks/users/useUserSettings';
import { useProfile } from 'src/context/providers/ProfileProvider';
import { useParams } from 'next/navigation';
import { REIMBURSEMENT_DATA_YEAR_STR } from 'src/components/constants';

interface ProfileReimbursementProps {
  location?: string;
  compact?: boolean;
}

const ProfileReimbursement = ({ location, compact = false }: ProfileReimbursementProps) => {
  const { entityDetails } = useProfile();
  const { type } = useParams<{ type: string }>();
  // An explicit location wins over the route param: in the profile drawer the
  // URL belongs to the host page, not to the entity being previewed, so
  // trusting `type` would read another entity type's code-set selection.
  const typeLocation = location || type || 'hcp';
  const { settings: userSettings, updateSettings } = useUserSettings();
  const { selectedFilterSet } = useProfileSelections(typeLocation);

  const isProvider = entityDetails?.type === 'hcp';

  const subHeader = isProvider
    ? `Includes only professional fees data from ${REIMBURSEMENT_DATA_YEAR_STR}; Indicates the allowed amount on a claim, prior to the application of any patient cost-sharing.`
    : `Includes only institutional fees data from ${REIMBURSEMENT_DATA_YEAR_STR}; Indicates the allowed amount on a claim, prior to the application of any patient cost-sharing.`;

  const { data: reimbursementData, isLoading } = useGetPayerMixReimbursement({
    providerId: entityDetails?.provider_id,
    location: typeLocation,
  });

  const [showAllCodes, setShowAllCodes] = useState(false);

  const topPayer = useMemo(() => {
    if (!reimbursementData?.results) return null;

    let topPayer = null;
    let highestReimbursement = 0;

    reimbursementData.results.forEach((item: any) => {
      item.payers.forEach((payer: any) => {
        // Only consider payers with valid reimbursement data (greater than 0)
        if (
          payer.avg_reimbursement_predicted &&
          payer.avg_reimbursement_predicted > 0 &&
          payer.avg_reimbursement_predicted > highestReimbursement
        ) {
          highestReimbursement = payer.avg_reimbursement_predicted;
          topPayer = {
            ...payer,
            code: item.code_name,
          };
        }
      });
    });

    return topPayer;
  }, [reimbursementData]);

  useEffect(() => {
    if (topPayer) {
      // Always set the top payer and site on every render when data is available
      updateSettings({
        selectedPayer: {
          payer_name: topPayer.payer_name,
          payer_type: topPayer.payer_type,
        },
        selectedSite: topPayer.soc_type,
      });
    }
  }, [topPayer, updateSettings]);

  const { displayData, hasMoreCards, totalCards } = useMemo(() => {
    if (!reimbursementData?.results || !userSettings?.selectedPayer) {
      return { displayData: [], hasMoreCards: false, totalCards: 0 };
    }

    const filteredData = reimbursementData?.results?.filter((item) => {
      const matchingPayer = item.payers.find(
        (payer) =>
          payer.payer_name?.toLowerCase() ===
            userSettings?.selectedPayer?.payer_name?.toLowerCase() &&
          payer.soc_type === userSettings?.selectedSite
      );

      return matchingPayer !== undefined;
    });

    const displayCount = showAllCodes ? filteredData.length : 6;

    return {
      displayData: filteredData.slice(0, displayCount),
      hasMoreCards: filteredData.length > displayCount,
      totalCards: filteredData.length,
    };
  }, [
    reimbursementData,
    userSettings?.selectedPayer,
    userSettings?.selectedSite,
    showAllCodes,
  ]);

  const isICD = selectedFilterSet?.detailed_filters?.some((filter) =>
    filter.type.toLowerCase().includes('icd')
  );

  const currentValue = useMemo(() => {
    const hasValue = selectedFilterSet;
    if (!hasValue) return '- All Claims';

    const name = selectedFilterSet?.label;
    return `- ${name} Claims`;
  }, [selectedFilterSet]);

  const hasSelectedValue = useMemo(() => {
    return !!selectedFilterSet;
  }, [selectedFilterSet]);

  const renderContent = () => {
    if (!reimbursementData?.results?.length && !hasSelectedValue) {
      const message = 'Select a Code Set to see results';
      return (
        <div className="flex items-center justify-center h-full min-h-48">
          {isLoading ? (
            <CircularProgress size="sm" color="neutral" />
          ) : (
            <Typography
              variant="body2"
              bold
              color="inherit"
              className="text-neutral-500"
            >
              {message}
            </Typography>
          )}
        </div>
      );
    }

    return (
      <>
        <div className="flex items-center gap-2 justify-between">
          <div className="flex items-center gap-2">
            <AllPayersSelect />
            <AllSitesSelect />
          </div>
        </div>

        {displayData.length === 0 ? (
          <div className="flex flex-1 items-center justify-center py-4">
            {isICD ? (
              <Typography className="text-sm font-semibold text-center text-tertiary">
                Reimbursement data is unavailable for diagnosis codes.
              </Typography>
            ) : (
              <NoReimbursementData allZero={true} />
            )}
          </div>
        ) : (
          <>
            <div className={`grid gap-4 py-4 ${compact ? 'grid-cols-2' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'}`}>
              {displayData.map((item: any) => {
                const payer = item.payers.find(
                  (payer: any) =>
                    payer.payer_name ===
                      userSettings?.selectedPayer?.payer_name &&
                    payer.soc_type === userSettings?.selectedSite
                );
                if (!payer) return null;
                return (
                  <ReimbursementCard
                    key={item.code}
                    compact={compact}
                    item={{
                      code: item.code_name,
                      ...payer,
                    }}
                  />
                );
              })}
            </div>
            <div className="flex items-center gap-4">
              <Typography variant="body2" color="primary">
                {`${displayData.length} of ${totalCards} Codes`}
              </Typography>

              <button
                disabled={!hasMoreCards}
                onClick={() => setShowAllCodes(!showAllCodes)}
                className="text-sm font-normal text-sky-900 bg-white hover:text-sky-600 transition-colors disabled:opacity-50 disabled:hover:text-sky-900"
              >
                Show All Codes
              </button>
            </div>
          </>
        )}
      </>
    );
  };

  return (
    <Card className={`bg-white h-full ${compact ? 'p-4' : 'p-6'}`}>
      <div className="flex flex-col h-full">
        <ProfileCardHeader
          icon={faMoneyCheckDollarPen}
          title="Contracted Reimbursement"
          hasSelectedSavedSearch={hasSelectedValue}
          currentSavedSearch={currentValue}
          subHeader={subHeader}
          isBeta={true}
        />
        {renderContent()}
      </div>
    </Card>
  );
};

export default ProfileReimbursement;
